import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Tooltip,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import { httpRequest } from '../api/helper';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { titleCase } from '../common/AppUtils'
import { useNotification } from '../context/NotificationContext';
import { API_ERROR_MESSAGE } from '../common/AppConstants';

const UploadResponse = ({ dataDict }) => {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mt: 2, maxHeight: 300, overflowY: 'auto' }}
    >
      {/* Summary Section */}
      {dataDict.summary && (
        <>
          <Typography variant="h5" gutterBottom color="primary">
            <strong>Summary</strong>
          </Typography>
          {Object.entries(dataDict.summary).map(([key, value], index) => (
            <Typography
              key={index}
              sx={{
                color:
                  key === "errors"
                    ? "red"
                    : key === "created"
                    ? "green"
                    : key === "updated"
                    ? "blue"
                    : "black",
                whiteSpace: 'pre-wrap', // Preserve line breaks
                fontFamily: "monospace",  // Optional for better visibility
              }}
            >
              {`${titleCase(key)}: ${JSON.stringify(value, null, 2)}`}
            </Typography>
          ))}
        </>
      )}

      {/* Detail Section */}
      {dataDict.detail && (
        <>
          <Typography variant="h5" gutterBottom sx={{ mt: 2 }} color="primary">
            <strong>Details</strong>
          </Typography>
          {dataDict.detail.length === 0 ? (
            <Typography>No row-level details available</Typography>
          ) : (
            dataDict.detail.map((row, idx) => (
              <Typography
                key={idx}
                sx={{
                  // whiteSpace: "pre-wrap", // Preserve line breaks
                  fontFamily: "monospace",  // Optional for better visibility
                }}
              >
                {`Row ${idx + 2}: ${JSON.stringify(row, null, 2)}`}
              </Typography>
            ))
          )}
        </>
      )}

      {/* Fallback error (if summary/detail missing) */}
      {!dataDict.summary && !dataDict.detail && dataDict.error && (
        <Typography color="red" sx={{ fontFamily: "monospace" }}>
          Error: {JSON.stringify(dataDict.error, null, 2)}
        </Typography>
      )}
    </Paper>
  );
};

export default function UploadModal({
  open,
  uploadTemplate,
  handleClose,
  handleUpload,
}) {
  const { addNotification } = useNotification();
  const [responseData, setResponseData] = useState({});
  const [file, setFile] = useState(null);

  useEffect(() => {
    setResponseData({});
    setFile(null);
  }, [open]);

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleUploadUtil = () => {
    if (!file) {
      addNotification('No file selected', 'warning');
      return;
    }
    handleUpload(file, onUploadSuccess, onUploadFailure);
  };

  const onUploadSuccess = (responseDict) => {
    console.log(responseDict);
    // Highlight response
    setResponseData(responseDict);
    setFile(null);
  };

  const onUploadFailure = (responseDict) => {
    console.log(responseDict);
    // Highlight errors
    setResponseData(responseDict);
    setFile(null);
  };

  const handleDownloadTemplate = async () => {
    await httpRequest({
      method: 'GET',
      url: 'common/download-template/',
      data: { filename: uploadTemplate },
      config: {
        responseType: 'blob',
      },
      onSuccess: (data) => {
        // Create a Blob (binary large object) from the response data
        const zipFileBlob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        // Generate a temporary URL for the Blob
        const blobUrl = window.URL.createObjectURL(zipFileBlob);
        // Create a hidden anchor (<a>) element for downloading the file
        const downloadLink = document.createElement('a');
        downloadLink.href = blobUrl;
        downloadLink.download = uploadTemplate; // Set the filename for the download
        // Append the anchor element to the document, trigger the download, then remove it
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
      },
      addNotification,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 24
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
        <Typography variant="h3">Upload Excel</Typography>
        {true && ( // ToDo: !isSubmitting
          <IconButton edge="end" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <Divider />

      <DialogContent>
        <Box
          component="form"
          noValidate
          sx={{
            display: 'flex',
            flexDirection: 'column',
            mb: 1,
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUploadIcon />}
              sx={{
                px: 6,
              }}
            >
              Browse
              <input
                type="file"
                accept=".xlsx, .xls"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            <Tooltip title={file ? file.name : ''}>
              <TextField
                fullWidth
                variant="outlined"
                label="File"
                value={file ? file.name : ''}
                InputProps={{ readOnly: true }}
                sx={{
                  '& fieldset': { border: 'none' }, // Removes border
                }}
              />
            </Tooltip>
          </Box>
        </Box>
        {Object.keys(responseData).length > 0 && (
          <UploadResponse dataDict={responseData} />
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleDownloadTemplate}
          startIcon={<DownloadIcon />}
          variant="outlined"
          color="primary"
        >
          Download Template
        </Button>
        <Button
          onClick={handleClose}
          variant="outlined"
          color="primary"
        >
          Cancel
        </Button>
        <Button
          onClick={handleUploadUtil}
          startIcon={<CloudUploadIcon />}
          disabled={!file}
          sx={{ px: 2 }}
          variant="contained"
          color="primary"
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
}
